import $ from "jquery";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Nav, Navbar, NavDropdown } from "react-bootstrap";
import OutsideClickHandler from "react-outside-click-handler";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import commonClient from "@/apis/commonClient";
import AppMessage from "@cleanup/components/AppMessage";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntRefreshIcon from "@icons/SntRefreshIcon";
import SntSearchIcon from "@icons/SntSearchIcon";
import SntLink from "@/components/ReactBootstrap/SntLink";
import moment from "@/constants/SensolusMoment";
import * as actions from "@/redux/actions";
import SearchInput from "../SearchPanel/SearchInput";
import SearchPanel from "../SearchPanel/SearchPanel";
import ProfileDropdownTitle from "./ProfileDropdownTitle";
import HeaderIconButton from "./HeaderIconButton";
import { RIGHT_SIDEBAR_TOGGLE_CLASS } from "@cleanup/widgets/RightSideBar/RightSideBar";
import SntQuestionIcon from "@icons/SntQuestionIcon";
import SntSmallMenu from "@icons/SntSmallMenu";
import Logo40Icon from "@images/logo40-40.png";
import SntAnnouncementscon from "@icons/SntAnnouncementscon";
import SntPersonalSettingsIcon from "@icons/SntPersonalSettingsIcon";
import AccountSwitchDialog from "@/components/AccountSwitchDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";
import AiIcon from "@cleanup/AiSummary/images/ai_loading_icon_small.svg";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { UserPermission } from "@/types/generated/UserPermission";
import { RootState } from "@/types/RootState";
import { translate } from "@/hooks/useLanguage";
import { isSystemOrPartner } from "@/utils/orgTypeUtils";

// The right-hand side of the header lays itself out by measurement, not by a
// viewport breakpoint: it keeps every secondary action as its own icon button
// for as long as they fit and folds only what is genuinely short of space into
// the kebab overflow menu (STIC-16077 - a fixed breakpoint collapsed them on
// screens with room to spare). The kebab is the last item in the row, directly
// before the profile avatar (STIC-16148). What the row gives up, in order:
// action icons right-to-left, then the search field down to a magnifier.
//
// One icon button is a 20px glyph with 8px padding either side, and the kebab
// is the same size - so folding away a *single* action buys nothing. The maths
// in measureHeaderActions() knows that and never collapses just one.
const HEADER_ICON_WIDTH = 36;
// Slack a folded-away item has to see on top of its own width before it comes
// back, so a sub-pixel layout change can't flip it in and out every frame.
const HEADER_RESTORE_BUFFER = 8;
// Expanded search field: the 240px input (#searchInputId in global-search.scss)
// plus its Nav.Item padding. Only used until the field has been measured once,
// i.e. when the header starts out too narrow to show it at all.
const SEARCH_FIELD_WIDTH_FALLBACK = 256;

const Styles = styled.div`
  .login-info .dropdown-menu {
    right: 0;
    left: auto;
  }

  .login-info > .dropdown-toggle {
    padding: 0px;
    margin-top: -2px;
    color: rgba(var(--bs-sensolus-rgb), var(--bs-text-opacity)) !important;
  }

  .header-overflow-menu > .dropdown-toggle {
    padding: 0;
    display: flex;
    align-items: center;
    color: rgba(var(--bs-sensolus-rgb), var(--bs-text-opacity)) !important;
  }

  .header-overflow-menu .dropdown-menu {
    right: 0;
    left: auto;
  }

  .git-dropdown .dropdown-menu {
    top: 48px;
    left: -150px;
    display: grid;
  }

  .git-dropdown .dropdown-menu .dropdown-item {
    display: grid;
  }

  .nav-item:hover {
    background-color: #e9ecef;
  }

  .nav-item.none-hover:hover {
    background-color: transparent;
  }

  /* Header button whose sidebar is open (STIC-16336). */
  .nav-item.active {
    background-color: #e9ecef;
  }
`;

interface GitInfo {
  gitBranch: string;
  gitBuildTime: string;
  gitBuildVersion: string;
  gitBuildNumber?: string;
  gitCommitId: string;
  gitCommitTime: string;
  gitDirty: boolean;
}

interface HeaderProps {
  reloadPage?: () => void;
}

const Header = ({ reloadPage }: HeaderProps) => {
  const header = useRef<HTMLDivElement>(null);
  const appMessageWraperRef = useRef<HTMLDivElement | null>(null);

  const [gitInfo, setGitInfo] = useState<GitInfo>({
    gitBranch: "",
    gitBuildTime: "",
    gitBuildVersion: "",
    gitCommitId: "",
    gitCommitTime: "",
    gitDirty: false,
  });

  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user);
  const {
    newNotificationsCount,
    showHelpSidebar = false,
    showFavoritesSidebar = false,
    showNotification = false,
    showAiChat = false,
  } = useSelector((state: RootState) => state.all, shallowEqual);
  const { buildVersion, organisation } = loginInfo;

  const { isMobile } = useSelector((state: RootState) => state.all);
  // How many of the secondary actions currently live in the kebab, and whether
  // the search field is down to its magnifier. Both are decided by
  // measureHeaderActions() below, never by a hardcoded width.
  const [collapsedActionCount, setCollapsedActionCount] = useState(0);
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const navSpacerRef = useRef<HTMLDivElement>(null);
  const searchItemRef = useRef<HTMLDivElement>(null);
  const searchFieldWidth = useRef(SEARCH_FIELD_WIDTH_FALLBACK);
  const measureRef = useRef<() => void>(() => {});
  // Mobile keeps its own search affordance (see STIC-15933); everywhere else
  // the magnifier is the last resort of the measuring pass.
  const searchAsIcon = isMobile || searchCollapsed;
  const { reloadAllFavorites } = useFavorites();
  const { disconnect, isConnected } = useWebSocket();

  const [searchInput, setSearchInput] = useState<string | null>(null);
  const [showAccountSwitchDialog, setShowAccountSwitchDialog] = useState(false);
  useEffect(() => {
    if (hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)) {
      commonClient.getBuildInfor().then(
        ({ data }) => {
          setGitInfo(data);
        },
        (err) => {
          console.log("Error when getting git system info", err);
        }
      );
    }
     
  }, []);

  const logoutClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    // Disconnect WebSocket before logout
    if (isConnected) {
      console.log("Disconnecting WebSocket before logout...");
      disconnect();
    }

    history.push("/logout");
  };

  // Help, favorites and notifications share the right-hand sidebar slot: each
  // header button toggles its own sidebar and closes the other two. (Their
  // RightSideBar ignores mousedowns on these buttons, see
  // RIGHT_SIDEBAR_TOGGLE_CLASS, so the toggle is not undone by its
  // outside-click close — STIC-16336.)
  const showRightSidebar = (sidebar: "help" | "favorites" | "notifications" | null) => {
    dispatch(actions.setShowHelpSidebar(sidebar === "help"));
    dispatch(actions.setShowFavoritesSidebar(sidebar === "favorites"));
    dispatch(actions.setShowGlobalNotification(sidebar === "notifications"));
  };

  const helpIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    showRightSidebar(showHelpSidebar ? null : "help");
  };

  const favoritesIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    showRightSidebar(showFavoritesSidebar ? null : "favorites");
  };

  const notificationsIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    showRightSidebar(showNotification ? null : "notifications");
  };

  const aiChatIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    dispatch(actions.setShowAiChat(!showAiChat));
  };

  const refreshIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    reloadPage?.();
    reloadAllFavorites?.();
  };

  useEffect(() => {
    const heightValue = $(appMessageWraperRef.current as Element).outerHeight() || 0;
    const height = heightValue + 50;

    $(header.current as Element).css({ height: height + "px" });
    $("#sidebar").css({ top: height + "px" });
  });

  const renderPlatformSubscription = () => {
    if (organisation && organisation.platformSubscription && !isSystemOrPartner(loginInfo)) {
      let _link = "#",
        _platformPlanText =
          translate(("platformplan_" +
            organisation.platformSubscription.basePlanKey +
            "_name") as LanguageKey),
        _cursor = "default";
      if (hasPermission(UserPermission.ORGANIZATION_VIEW)) {
        _link = `/organization_update?id=${organisation.id}&tabActive=enabled_product_plan_tab`;
        _cursor = "pointer";
      }

      if (organisation.platformSubscription.trial) {
        const _now = moment();
        const _days =
          moment(organisation.platformSubscription.tillDate).diff(
            _now,
            "days"
          ) + 1;
        _platformPlanText += ` TRIAL (${_days} DAYS LEFT)`;
      }

      return (
        <>
          {_platformPlanText && (
            <div id="trial-header">
              <SntLink
                className="text-sensolus fw-bold"
                to={_link}
                style={{
                  // No left offset: `#header-brand` + `#header-org-selector`
                  // already place this at the content column's left edge, so
                  // the plan reads on the same line as the page title.
                  textTransform: "uppercase",
                  cursor: _cursor,
                }}
              >
                {_platformPlanText}
              </SntLink>
            </div>
          )}
        </>
      );
    }
  };

  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // Secondary header actions. Each is its own icon button for as long as the row
  // has the space; whatever is left over folds into the kebab, right-to-left, so
  // the first entry here is the last one to disappear. The bell and the profile
  // avatar are deliberately NOT in here — they stay visible at every width
  // (time-sensitive notifications + identity/logout anchor).
  const overflowActions: {
    key: string;
    title: string;
    icon: React.ReactNode;
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    active: boolean;
  }[] = [];

  if (hasFeature(FeatureKey.ai_chat) && hasPermission(UserPermission.AI_CHAT_USE)) {
    overflowActions.push({
      key: "ai",
      title: translate(LanguageKey.ai_chat_title_key),
      icon: <img alt="AI Chat" src={AiIcon} style={{ width: 20, height: 20 }} />,
      onClick: aiChatIconClick,
      active: showAiChat,
    });
  }
  overflowActions.push({
    key: "favorites",
    title: translate(LanguageKey.favorites_key),
    icon: (
      <IcomoonIcon
        icon="star-empty"
        size={20}
        style={{ marginTop: "-4px", color: "currentColor" }}
      />
    ),
    onClick: favoritesIconClick,
    active: showFavoritesSidebar,
  });
  overflowActions.push({
    key: "refresh",
    title: translate(LanguageKey.header_refresh_title_key),
    icon: <SntRefreshIcon size={20} />,
    onClick: refreshIconClick,
    active: false,
  });
  overflowActions.push({
    key: "help",
    title: translate(LanguageKey.help_key),
    icon: <SntQuestionIcon size={20} />,
    onClick: helpIconClick,
    active: showHelpSidebar,
  });

  const totalActions = overflowActions.length;
  const hiddenActionCount = Math.min(collapsedActionCount, totalActions);
  const shownActions = overflowActions.slice(0, totalActions - hiddenActionCount);
  const hiddenActions = overflowActions.slice(totalActions - hiddenActionCount);
  // Everything folded away and the search down to a magnifier: this column has
  // nothing left to give. It stops shrinking at that point so any remaining
  // shortfall lands on the org pill — which truncates its name — instead of
  // pushing the icons off the end of the row. Until then it stays shrinkable,
  // because that squeeze is what the measuring pass reads as the shortfall.
  const navAtFloor = hiddenActionCount >= totalActions && searchAsIcon;

  // Decide, from the row's real geometry, how much of it has to fold away. Runs
  // before paint (useLayoutEffect) and re-runs on each state change it makes,
  // so it settles in a couple of passes without the intermediate layouts ever
  // reaching the screen.
  const measureHeaderActions = useCallback(() => {
    const nav = navRef.current;
    const spacer = navSpacerRef.current;
    if (!nav || !spacer) return;

    const searchItem = searchItemRef.current;
    if (searchItem && !searchAsIcon) {
      searchFieldWidth.current = searchItem.offsetWidth;
    }

    // The spacer soaks up everything unused and is the first thing squeezed to
    // zero, so at most one of these two is non-zero: slack is the signed room
    // left in the row (negative once the row no longer fits).
    const overflowPx = Math.max(0, nav.scrollWidth - nav.clientWidth);
    const slack = spacer.offsetWidth - overflowPx;

    const shown = totalActions - hiddenActionCount;
    const stripWidth = (shown + (hiddenActionCount > 0 ? 1 : 0)) * HEADER_ICON_WIDTH;
    // Everything the action strip is allowed to spend: what it holds now, plus
    // (or minus) the room the rest of the row has left over.
    const budget = slack + stripWidth;
    // How many actions fit in `px`, keeping one slot for the kebab unless all
    // of them fit and it isn't needed at all.
    const fits = (px: number) =>
      px >= totalActions * HEADER_ICON_WIDTH
        ? totalActions
        : Math.max(
            0,
            Math.min(totalActions, Math.floor(px / HEADER_ICON_WIDTH) - 1)
          );

    const shrinkTo = fits(budget);
    if (shrinkTo < shown) {
      setCollapsedActionCount(totalActions - shrinkTo);
      return;
    }

    if (slack < 0) {
      // Every action is already in the kebab and the row still doesn't fit:
      // the search field is what's left to give.
      if (!searchCollapsed && !isMobile) setSearchCollapsed(true);
      return;
    }

    // Room to spare — hand it back in the reverse order it was taken, so the
    // search field wins over the icons it was sacrificed for. Folding the
    // visible icons back into the kebab is part of what pays for it.
    if (searchCollapsed) {
      const magnifierWidth = searchItem?.offsetWidth ?? HEADER_ICON_WIDTH;
      const recoverable = stripWidth - Math.min(totalActions, 1) * HEADER_ICON_WIDTH;
      const searchCost = searchFieldWidth.current - magnifierWidth;
      if (slack + recoverable >= searchCost + HEADER_RESTORE_BUFFER) {
        setSearchCollapsed(false);
        return;
      }
    }

    const growTo = fits(budget - HEADER_RESTORE_BUFFER);
    if (growTo > shown) setCollapsedActionCount(totalActions - growTo);
  }, [
    hiddenActionCount,
    isMobile,
    searchAsIcon,
    searchCollapsed,
    totalActions,
  ]);

  // Deliberately NOT dependency-less: reading scrollWidth/offsetWidth forces a
  // layout, and this component re-renders on things that cannot change the row's
  // geometry (every WebSocket message, for one — WebSocketContext hands out a
  // fresh value object per message). The deps here are exactly the state this
  // pass controls, which is what a multi-pass settle needs; everything else that
  // can move the geometry arrives through the observer below.
  useLayoutEffect(() => {
    measureRef.current = measureHeaderActions;
    measureHeaderActions();
  }, [measureHeaderActions]);

  // Geometry can change without this component re-rendering: the org pill and
  // the support-login button render into their slots through portals, the plan
  // label arrives with the login, the sidebar column can change width. Watching
  // the spacer catches all of it — it holds whatever the row has left over, so
  // its width moves whenever anything else in the row does. The Nav covers the
  // other half: once the row overflows the spacer is pinned at 0 and only the
  // Nav's own box keeps shrinking.
  //
  // The callback re-measures in the next frame rather than inline: folding an
  // icon resizes the spacer, and resizing an observed element from inside its
  // own observer is what produces "ResizeObserver loop" errors (which Vite's
  // overlay surfaces in dev). One observer for the component's life — it calls
  // through the ref, which always holds the latest measuring pass.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => measureRef.current());
    });
    if (navRef.current) observer.observe(navRef.current);
    if (navSpacerRef.current) observer.observe(navSpacerRef.current);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        // 1004, not 1003: page-content chrome sits at 1003 (ListSaveView's views
        // dropdown, Gl3dLayerControl) and, being later in the DOM, used to paint
        // over the header's own overlays - the search panel showed the views
        // caret bleeding through it. Still below .sidebar (1010).
        zIndex: 1004,
      }}
      id="header"
      className="snt-header"
      ref={header}
    >
      <>
        <div ref={appMessageWraperRef}>
          <AppMessage />
        </div>

        <Styles>
          {/* flex-nowrap: the header is one fixed-height row, so a wrap
              would push items out of sight instead of showing up as the
              overflow measureHeaderActions() reacts to. */}
          <Navbar bg="light" className="mb-3 text-sensolus flex-nowrap">
            <Nav.Item
              className="sidebar-menu-icon d-block d-md-none"
              title={translate(LanguageKey.menu_name_key)}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                dispatch(actions.setShowCanvasMenu((pre: boolean) => !pre));
              }}
              role="button"
              style={{ width: 50, height: 49, position: "relative" }}
            >
              <div className="position-absolute top-50 start-50 translate-middle">
                <IcomoonIcon icon="menu" size="24"></IcomoonIcon>
              </div>
            </Nav.Item>

            {/* System users get the Sensolus logo here; partners get their own
                logo in the same slot (STIC-15365 — their sidebar is no longer
                full-height, so it can't carry it). Normal orgs still show it on
                the branded sidebar instead (see Sidebar.tsx).

                This block also provides the left offset that keeps
                #header-org-selector clear of the sidebar column. */}
            {/* The sidebar's column, reserved in the header.
                Every login has a full-height coloured sidebar carrying its logo
                (Sidebar.tsx), and that sidebar covers this corner. What the
                header needs here is only the matching offset, so everything
                after it — the org pill, the platform plan — begins where the
                content column begins and lines up with the page title.
                Width from `#header-brand` in header.scss, which reads the same
                variable the sidebar does. */}
            {!isMobile && (
              <div
                id="header-brand"
                className="d-none d-md-block"
                style={{ flexShrink: 0 }}
              />
            )}

            <div
              id="header-org-selector"
              className="d-none d-md-flex align-items-center header-org-selector"
              // paddingLeft comes from `#header-org-selector` in header.scss.
              // minWidth 0 so the pill inside can shrink past its own text and
              // truncate (the Nav gives way first — see navAtFloor).
              style={{ height: 49, minWidth: 0 }}
            />
            <div
              id="header-support-login"
              className="d-none d-md-flex align-items-center"
            />

            {/* The plan label keeps its width: letting it wrap its words under
                pressure would both break the single-line row and absorb the
                squeeze the Nav needs to see to fold its own icons away. */}
            <div
              className="header-info d-none d-lg-block"
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
            >
              <div style={{ alignItems: "center", display: "flex" }}>
                {renderPlatformSubscription()}
              </div>
            </div>

            {isMobile && loginInfo.custumInfo === undefined && (
              <Nav.Item className="ms-1">
                <img src={Logo40Icon} alt={"Logo"} />
              </Nav.Item>
            )}

            <Nav
              ref={navRef}
              // No justify-content-end: the spacer below does the right
              // alignment instead. Pushing from the end would make an overflow
              // spill off the *start* edge, which scrollWidth cannot see - and
              // an invisible overflow is exactly what the measuring pass needs
              // to react to. minWidth 0 lets this column be squeezed by the
              // rest of the row rather than pushing the navbar wider than the
              // viewport: that squeeze is the overflow it reads.
              className="flex-grow-1 flex-nowrap"
              style={{
                alignItems: "center",
                minWidth: 0,
                flexShrink: navAtFloor ? 0 : 1,
              }}
            >
              {/* Holds whatever room the row has left over, and right-aligns
                  everything after it. It is the only shrinkable item here, so
                  its width is the slack, and a scrollWidth overshoot past it is
                  the shortfall. */}
              <div
                ref={navSpacerRef}
                aria-hidden="true"
                style={{ flex: "1 1 auto", minWidth: 0, height: 1 }}
              />
              <Nav.Item
                ref={searchItemRef}
                className={`align-items-center ${!isMobile ? "none-hover" : ""}`}
                style={{
                  padding: "0.5rem 8px",
                  fontSize: 20,
                  height: 49,
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                {searchAsIcon ? (
                  // Compact/mobile: a magnifier that opens the same search panel
                  // (recent + results). The panel is anchored to this item (see
                  // #search-panel right-align in global-search.scss).
                  <SntSearchIcon
                    size={20}
                    role="button"
                    onClick={(ev: React.MouseEvent<SVGSVGElement>) => {
                      ev.preventDefault();
                      setShowSearchPanel((prev) => !prev);
                    }}
                  />
                ) : (
                  <div style={{ position: "relative" }}>
                    <SearchInput
                      onChange={(e: string | null) => setSearchInput(e)}
                      setShowSearchPanel={(e: boolean) => setShowSearchPanel(e)}
                      id="searchInputId"
                    />
                  </div>
                )}
                <div
                  className={`background-black snt-background-black ${
                    showSearchPanel ? "visible" : "none"
                  }`}
                />
                <OutsideClickHandler
                  onOutsideClick={(e: MouseEvent) => {
                    if (showSearchPanel && (e.target as HTMLElement).id !== "searchInputId") {
                      setShowSearchPanel(false);
                    }
                  }}
                >
                  {showSearchPanel ? (
                    <SearchPanel
                      searchText={searchInput}
                      closePopup={() => setShowSearchPanel(false)}
                      showSearchInput={searchAsIcon}
                    />
                  ) : (
                    ""
                  )}
                </OutsideClickHandler>
              </Nav.Item>

              {/* Every action the row has room for, as its own icon button. */}
              {shownActions.map((action) => (
                <HeaderIconButton
                  key={action.key}
                  title={action.title}
                  onClick={action.onClick}
                  active={action.active}
                >
                  {action.icon}
                </HeaderIconButton>
              ))}

              <HeaderIconButton
                title={translate(LanguageKey.notifications_key)}
                onClick={notificationsIconClick}
                active={showNotification}
                badge={newNotificationsCount}
              >
                <SntAnnouncementscon size={20} />
              </HeaderIconButton>

              {/* Whatever didn't fit, in the kebab — last in the row, directly
                  before the profile avatar (STIC-16148). */}
              {hiddenActions.length > 0 && (
                <NavDropdown
                  className={`d-flex justify-content-center align-items-center no-caret header-overflow-menu ${RIGHT_SIDEBAR_TOGGLE_CLASS}`}
                  style={{ padding: "0 8px", height: "49px" }}
                  title={<SntSmallMenu size={20} />}
                >
                  {hiddenActions.map((action) => (
                    <NavDropdown.Item
                      key={action.key}
                      bsPrefix="dropdown-item nav-link"
                      onClick={action.onClick}
                    >
                      <span
                        className="me-2 d-inline-flex align-items-center justify-content-center"
                        style={{ width: 20 }}
                      >
                        {action.icon}
                      </span>
                      {action.title}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              )}
              <NavDropdown
                className={`d-flex justify-content-center align-items-center no-caret login-info ${
                  isMobile ? "" : "me-3"
                }`}
                style={{ padding: "0 8px", height: "49px" }}
                title={<ProfileDropdownTitle loginInfo={loginInfo} />}
              >
                <div>
                  <NavDropdown.Item
                    bsPrefix="dropdown-item nav-link"
                    onClick={() => {
                      const currentPathWithQuery =
                        window.location.pathname + window.location.search;

                      // Save it to sessionStorage
                      sessionStorage.setItem(
                        "PROFILE_PREV_PATH",
                        currentPathWithQuery
                      );

                      history.push("/profile");
                    }}
                  >
                    <span className="me-2">
                      <SntPersonalSettingsIcon />
                    </span>
                    {translate(LanguageKey.personal_settings_key)}
                  </NavDropdown.Item>
                  {loginInfo.availableAccounts && loginInfo.availableAccounts.length > 1 && (
                      <NavDropdown.Item
                        bsPrefix="dropdown-item nav-link"
                        onClick={() => setShowAccountSwitchDialog(true)}
                      >
                        <FontAwesomeIcon
                          icon={faArrowRightArrowLeft}
                          className="me-2"
                        />
                        {translate(LanguageKey.switch_account_key)}
                      </NavDropdown.Item>
                    )}
                  <NavDropdown.Item
                    bsPrefix="dropdown-item nav-link"
                    onClick={logoutClick}
                  >
                    <IcomoonIcon
                      icon="exit"
                      size="16"
                      className="me-2"
                    ></IcomoonIcon>
                    {translate(LanguageKey.header_logout_key)}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  {hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) ? (
                    <NavDropdown
                      bsPrefix="dropdown-item nav-link"
                      className="git-dropdown"
                      title={
                        <>
                          <IcomoonIcon
                            icon="info"
                            className="me-2"
                          ></IcomoonIcon>
                          {"Version " + gitInfo.gitBuildVersion}
                        </>
                      }
                    >
                      <NavDropdown.Item>
                        <b>Build Version:</b> {gitInfo.gitBuildVersion}
                      </NavDropdown.Item>
                      {gitInfo.gitBuildNumber && (
                        <NavDropdown.Item>
                          <b>Build Number:</b> #{gitInfo.gitBuildNumber}
                        </NavDropdown.Item>
                      )}
                      <NavDropdown.Item>
                        <b>Git Branch:</b> {gitInfo.gitBranch}
                      </NavDropdown.Item>
                      <NavDropdown.Item>
                        <b>Build Time:</b>{" "}
                        {gitInfo.gitBuildTime
                          ? moment(gitInfo.gitBuildTime).format("YYYY-MM-DD HH:mm:ss")
                          : ""}
                      </NavDropdown.Item>
                      <NavDropdown.Item>
                        <b>Commit ID:</b> {gitInfo.gitCommitId?.substring(0, 12)}
                      </NavDropdown.Item>
                      <NavDropdown.Item>
                        <b>Commit Time:</b>{" "}
                        {gitInfo.gitCommitTime
                          ? moment(gitInfo.gitCommitTime).format("YYYY-MM-DD HH:mm:ss")
                          : ""}
                      </NavDropdown.Item>
                      <NavDropdown.Item>
                        <b>Git Status:</b>{" "}
                        {gitInfo.gitDirty ? "dirty" : "clean"}
                      </NavDropdown.Item>
                    </NavDropdown>
                  ) : (
                    <NavDropdown.Item bsPrefix="nav-item nav-link">
                      <IcomoonIcon icon="info" className="me-2"></IcomoonIcon>
                      {"Version " + buildVersion}
                    </NavDropdown.Item>
                  )}
                </div>
              </NavDropdown>
            </Nav>
          </Navbar>
        </Styles>
      </>
      <AccountSwitchDialog
        show={showAccountSwitchDialog}
        onHide={() => setShowAccountSwitchDialog(false)}
        currentUser={loginInfo}
        openedFrom="header"
      />
    </div>
  );
};

export default Header;
